/**
 * useUniversalSearch - TanStack Query hook for universal search
 *
 * Debounces the search query by 300ms, runs searchRamp(),
 * and returns grouped results for the command palette.
 */

import { useQuery } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { useTenant } from '@/contexts'
import { useAppContext } from '@/hooks'
import { searchRamp, type SearchResults } from '@/lib/api/universal-search'

const EMPTY_RESULTS: SearchResults = { aircraft: [], clients: [], spaces: [] }

export function useUniversalSearch(query: string) {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id
  const { locationId } = useAppContext()
  const [debouncedQuery, setDebouncedQuery] = useState(query)

  // Debounce the query
  useEffect(() => {
    if (query.length < 2) {
      setDebouncedQuery('')
      return
    }
    const timer = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(timer)
  }, [query])

  const result = useQuery({
    queryKey: ['universal-search', debouncedQuery, locationId],
    queryFn: () => searchRamp(debouncedQuery, organizationId!, locationId),
    enabled: debouncedQuery.length >= 2 && !!organizationId && !!locationId,
    staleTime: 30_000, // 30s cache
    placeholderData: EMPTY_RESULTS,
  })

  return {
    results: result.data ?? EMPTY_RESULTS,
    isLoading: result.isFetching,
    isEmpty: debouncedQuery.length >= 2 && !result.isFetching &&
      (result.data?.aircraft.length === 0 &&
       result.data?.clients.length === 0 &&
       result.data?.spaces.length === 0),
  }
}
